import { useEffect, useRef, useState } from "react";
import { Check, LogOut, Pencil } from "lucide-react";
import AsteriskMark from "./AsteriskMark";
import { NAV_ROUTES, isNavItemActive } from "../navItems";
import styles from "./Sidebar.module.css";

function isTypingTarget(target) {
  return target.isContentEditable || ["INPUT", "TEXTAREA", "SELECT"].includes(target.tagName);
}

export default function Sidebar({ activePath, navItems, onNavigate, user, onLogout }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const accountRef = useRef(null);

  // Letter keys jump between pages, matching the shortcut shown in each tooltip
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.metaKey || event.ctrlKey || event.altKey || isTypingTarget(event.target)) {
        return;
      }
      const key = event.key.toUpperCase();
      const route = NAV_ROUTES.find((navRoute) => navRoute.shortcut === key);
      if (route) {
        onNavigate(route.path);
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [onNavigate]);

  useEffect(() => {
    if (!menuOpen) {
      return undefined;
    }

    const handlePointerDown = (event) => {
      if (!accountRef.current?.contains(event.target)) {
        setMenuOpen(false);
      }
    };
    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        setMenuOpen(false);
      }
    };

    document.addEventListener("pointerdown", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("pointerdown", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [menuOpen]);

  return (
    <aside className={`${styles.sidebar} d-flex flex-column align-items-center`}>
      <button
        type="button"
        className={styles.brand}
        onClick={() => onNavigate("/overview")}
        aria-label="PropFunding admin home"
      >
        <AsteriskMark className={styles.mark} />
      </button>

      <nav className={`${styles.nav} d-flex flex-column align-items-center`} aria-label="Main">
        {navItems.map((item) => {
          const { path, label, icon: Icon, shortcut, children } = item;
          const isActive = isNavItemActive(item, activePath);

          return (
            <div key={label} className={styles.navGroup}>
              <button
                type="button"
                className={`${styles.navItem} ${isActive ? styles.navItemActive : ""}`}
                onClick={() => onNavigate(path)}
                aria-label={label}
                aria-current={isActive ? "page" : undefined}
                aria-keyshortcuts={shortcut}
              >
                <Icon size={21.6} strokeWidth={1.5} aria-hidden="true" />
              </button>

              {children ? (
                <div className={styles.flyout}>
                  <div className={`${styles.flyoutPanel} d-flex flex-column`} aria-label={label}>
                    {children.map((child) => {
                      const isChildActive = child.path === activePath;

                      return (
                        <button
                          key={child.path}
                          type="button"
                          className={`${styles.flyoutItem} ${
                            isChildActive ? styles.flyoutItemActive : ""
                          } d-flex align-items-center justify-content-between`}
                          onClick={() => onNavigate(child.path)}
                          aria-current={isChildActive ? "page" : undefined}
                          aria-keyshortcuts={child.shortcut}
                        >
                          {child.label}
                          {isChildActive ? <Check size={14} strokeWidth={2} aria-hidden="true" /> : null}
                        </button>
                      );
                    })}
                  </div>
                </div>
              ) : (
                <span className={`${styles.tooltip} ${styles.tooltipSimple} d-flex align-items-center`} role="tooltip">
                  Go to {label}
                  <kbd className={styles.shortcut}>{shortcut}</kbd>
                </span>
              )}
            </div>
          );
        })}
      </nav>

      <div className={styles.account} ref={accountRef}>
        <button
          type="button"
          className={styles.avatar}
          onClick={() => setMenuOpen((open) => !open)}
          aria-haspopup="menu"
          aria-expanded={menuOpen}
          aria-label="Account menu"
        >
          {user.name[0]}
        </button>

        {menuOpen ? (
          <div className={styles.menu} role="menu">
            <div className={`${styles.menuName} d-flex align-items-center`}>
              {user.name}
              <button type="button" className={styles.iconButton} aria-label="Edit profile">
                <Pencil size={16} strokeWidth={1.75} />
              </button>
            </div>
            <p className={styles.menuEmail}>{user.email}</p>
            <button
              type="button"
              role="menuitem"
              className={`${styles.logout} d-flex align-items-center`}
              onClick={onLogout}
            >
              <LogOut size={16} strokeWidth={1.75} aria-hidden="true" />
              Log out
            </button>
          </div>
        ) : null}
      </div>
    </aside>
  );
}
